import { Head } from "@inertiajs/react"

import { HpiDataTable, HpiStatsBar } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate, formatDateRange } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface EmployeeInfo {
  id: number
  name: string
  pay_rate: number | null
  worker_type: number
}

interface EmployeeSummary {
  user_id: number | null
  name: string
  pay_rate: number | null
  otj_minutes: number
  travel_minutes: number
  regular_minutes: number
  overtime_minutes: number
  time_pay: number
  flat_rate_total: number
  mileage: number
  mileage_total: number
  expenses: number
  pto_hours: number
  sto_hours: number
}

interface PayPeriod {
  week1_start: string
  week2_end: string
  employees: EmployeeSummary[]
  totals: EmployeeSummary | null
}

interface TimeEntry {
  id: number
  case_id: number | null
  date: string | null
  case_name: string | null
  service_type: string | null
  hours: number | null
  travel_hours: number | null
  amount: number
  paid: boolean
}

interface Expense {
  id: number
  case_id: number | null
  date: string | null
  case_name: string | null
  expense_type: string | null
  description: string | null
  amount: number
  paid: boolean
}

interface MileageEntry {
  id: number
  case_id: number | null
  date: string | null
  case_name: string | null
  miles: number
  rate: number | null
  amount: number
  paid: boolean
}

interface PtoRecord {
  id: number
  start_date: string | null
  end_date: string | null
  request_type: number
  hours: number | null
  status: number
}

interface Props {
  employee: EmployeeInfo
  pay_period: PayPeriod
  time_entries: TimeEntry[]
  expenses: Expense[]
  mileage_entries: MileageEntry[]
  pto_records: PtoRecord[]
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function formatMinutes(minutes: number): string {
  if (minutes === 0) return ""
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${String(m).padStart(2, "0")}m`
  if (m === 0) return `${h}h`
  return `${h}h ${String(m).padStart(2, "0")}m`
}

/* ── Columns ───────────────────────────────────────────── */

const timeColumns: HpiColumn<TimeEntry>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">
        {row.case_id ? (
          <a
            href={`/cases/${row.case_id}/time_entries/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue hover:underline"
          >
            {formatDate(row.date)}
          </a>
        ) : (
          formatDate(row.date)
        )}
      </div>
    ),
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Service",
    render: (row) => <div className="text-sm">{row.service_type ?? "-"}</div>,
  },
  {
    header: "Hours",
    width: "max-w-[80px]",
    render: (row) => (
      <div className="text-right text-sm">{row.hours?.toFixed(2) ?? "-"}</div>
    ),
  },
  {
    header: "Travel",
    width: "max-w-[80px]",
    render: (row) => (
      <div className="text-right text-sm">
        {row.travel_hours && row.travel_hours > 0
          ? row.travel_hours.toFixed(2)
          : "-"}
      </div>
    ),
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm font-medium">
        {formatCurrency(row.amount)}
      </div>
    ),
  },
  {
    header: "Paid",
    width: "max-w-[60px]",
    render: (row) => (
      <div
        className={`text-sm ${row.paid ? "text-hpi-green" : "text-hpi-red"}`}
      >
        {row.paid ? "Yes" : "No"}
      </div>
    ),
  },
]

const expenseColumns: HpiColumn<Expense>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">
        {row.case_id ? (
          <a
            href={`/cases/${row.case_id}/expenses/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue hover:underline"
          >
            {formatDate(row.date)}
          </a>
        ) : (
          formatDate(row.date)
        )}
      </div>
    ),
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Type",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{row.expense_type ?? "-"}</div>,
  },
  {
    header: "Description",
    render: (row) => <div className="text-sm">{row.description ?? "-"}</div>,
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm font-medium">
        {formatCurrency(row.amount)}
      </div>
    ),
  },
  {
    header: "Paid",
    width: "max-w-[60px]",
    render: (row) => (
      <div
        className={`text-sm ${row.paid ? "text-hpi-green" : "text-hpi-red"}`}
      >
        {row.paid ? "Yes" : "No"}
      </div>
    ),
  },
]

const mileageColumns: HpiColumn<MileageEntry>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">
        {row.case_id ? (
          <a
            href={`/cases/${row.case_id}/time_entries/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue hover:underline"
          >
            {formatDate(row.date)}
          </a>
        ) : (
          formatDate(row.date)
        )}
      </div>
    ),
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Miles",
    width: "max-w-[80px]",
    render: (row) => <div className="text-right text-sm">{row.miles}</div>,
  },
  {
    header: "Rate",
    width: "max-w-[80px]",
    render: (row) => (
      <div className="text-right text-sm">{formatCurrency(row.rate ?? 0)}</div>
    ),
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm font-medium">
        {formatCurrency(row.amount)}
      </div>
    ),
  },
  {
    header: "Paid",
    width: "max-w-[60px]",
    render: (row) => (
      <div
        className={`text-sm ${row.paid ? "text-hpi-green" : "text-hpi-red"}`}
      >
        {row.paid ? "Yes" : "No"}
      </div>
    ),
  },
]

const ptoColumns: HpiColumn<PtoRecord>[] = [
  {
    header: "Start",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">
        {row.id ? (
          <a
            href={`/pto_requests/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue hover:underline"
          >
            {formatDate(row.start_date)}
          </a>
        ) : (
          formatDate(row.start_date)
        )}
      </div>
    ),
  },
  {
    header: "End",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{formatDate(row.end_date)}</div>,
  },
  {
    header: "Type",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-sm">
        {row.request_type === 0
          ? "Vacation"
          : row.request_type === 1
            ? "Sick"
            : "Other"}
      </div>
    ),
  },
  {
    header: "Hours",
    width: "max-w-[80px]",
    render: (row) => (
      <div className="text-right text-sm">{row.hours?.toFixed(1) ?? "-"}</div>
    ),
  },
]

/* ── Main Component ────────────────────────────────────── */

export default function EmployeeDetail({
  employee,
  pay_period,
  time_entries,
  expenses,
  mileage_entries,
  pto_records,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Accounting", href: "/accounting/expenses" },
    { title: "Payroll", href: "/accounting/payroll" },
    {
      title: employee.name,
      href: `/accounting/payroll/detail?employee_id=${employee.id}`,
    },
  ]

  const empSummary = pay_period.employees.find((e) => e.user_id === employee.id)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Payroll - ${employee.name}`} />

      <div className="p-6">
        <HpiStatsBar
          stats={[
            {
              label: "Employee",
              subLabel: employee.worker_type === 0 ? "W2" : "1099",
              value: employee.name,
            },
            {
              label: "Pay Rate",
              subLabel: "/hr",
              value: employee.pay_rate
                ? formatCurrency(employee.pay_rate)
                : "-",
            },
            {
              label: "Period",
              subLabel: "Current",
              value: formatDateRange(
                pay_period.week1_start,
                pay_period.week2_end,
              ),
            },
            ...(empSummary
              ? [
                  {
                    label: "Reg",
                    subLabel: "Time",
                    value: formatMinutes(empSummary.regular_minutes),
                  },
                  {
                    label: "OT",
                    subLabel: "Time",
                    value: formatMinutes(empSummary.overtime_minutes),
                  },
                  {
                    label: "Time Pay",
                    subLabel: "Total",
                    value: formatCurrency(empSummary.time_pay),
                  },
                ]
              : []),
          ]}
          className="mb-6"
        />

        {time_entries.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<TimeEntry>
              title={`Time Entries (${time_entries.length})`}
              columns={timeColumns}
              data={time_entries}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {mileage_entries.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<MileageEntry>
              title={`Mileage (${mileage_entries.length})`}
              columns={mileageColumns}
              data={mileage_entries}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {expenses.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<Expense>
              title={`Expenses (${expenses.length})`}
              columns={expenseColumns}
              data={expenses}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {pto_records.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<PtoRecord>
              title={`PTO (${pto_records.length})`}
              columns={ptoColumns}
              data={pto_records}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {time_entries.length === 0 &&
          mileage_entries.length === 0 &&
          expenses.length === 0 &&
          pto_records.length === 0 && (
            <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
              No payroll data for this period.
            </div>
          )}
      </div>
    </AppLayout>
  )
}
